import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { XIcon } from '@phosphor-icons/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateModel } from 'api/models';
import { useFeedback } from 'contexts/feedback';
import TextInput from 'components/Form/TextInput';
import SelectInput from 'components/Form/SelectInput';
import Button from 'components/Button';

import { MODEL_OPTIONS, EMBEDDING_OPTIONS } from 'helpers/models.js';

const TYPE_OPTIONS = [
    { value: 'model', label: 'Model' },
    { value: 'embedding', label: 'Embedding' },
];

export default function ModelEditPopup({
    visible,
    setVisible,
    model
}) {

    const { modelId } = useParams();
    const cache = useQueryClient();
    const { setSuccess, setError } = useFeedback();
    const [selectedType, setSelectedType] = useState(model?.type || 'model');

    useEffect(() => {
        if (model?.type) {
            setSelectedType(model.type);
        }
    }, [model?.type]);

    const { mutate: update } = useMutation({
        mutationKey: ['updateModel'],
        mutationFn: updateModel,
        onSuccess: () => {
            cache.invalidateQueries(['models']);
            setSuccess("Modello aggiornato con successo");
            return setVisible(false);
        },
        onError: (error) => {
            setError("Errore durante l'aggiornamento del modello");
            console.error("Error updating model:", error);
        }
    });

    function onSubmitHandler(e) {
        e.preventDefault();

        const formData = {
            name: e.target.name.value || model?.name,
            model: e.target.model.value || model?.model,
            apiKey: e.target.apiKey.value || undefined,
            baseUrl: e.target.baseUrl.value,
            enabled: e.target.enabled.checked,
            type: e.target.type.value || model?.type,
        }

        return update({
            id: modelId,
            data: formData
        });
    }

    return (
        <div className={`fixed top-0 left-0 w-full h-full bg-white/90 bg-opacity-50 z-[var(--z-modal)] transition-all duration-300 ${visible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
            <div className='bg-white rounded-sm border border-light w-full max-w-lg mx-4 md:mx-auto mt-20 p-6 max-h-[80vh] overflow-y-auto'>
                <div className='flex justify-between items-center pb-4'>
                    <p className='text-lg font-semibold'>Modifica modello</p>
                    <div onClick={() => setVisible(false)} className='cursor-pointer text-muted hover:text-black'>
                        <XIcon size={20} />
                    </div>
                </div>

                <form onSubmit={onSubmitHandler} className='flex flex-col gap-4'>
                    <TextInput
                        label="Nome"
                        name="name"
                        placeholder="Nome identificativo del modello"
                        defaultValue={model?.name}
                        required
                    />
                    <SelectInput
                        label="Tipo"
                        name="type"
                        options={TYPE_OPTIONS}
                        defaultValue={model?.type || 'model'}
                        onChange={(e) => setSelectedType(e.target.value)}
                    />
                    <SelectInput
                        label="Modello"
                        name="model"
                        options={selectedType === 'embedding' ? EMBEDDING_OPTIONS : MODEL_OPTIONS}
                        defaultValue={model?.model}
                    />
                    <TextInput
                        label="API Key"
                        name="apiKey"
                        type="password"
                        placeholder="Lascia vuoto per mantenere l'attuale"
                    />
                    <TextInput
                        label="Base URL"
                        name="baseUrl"
                        placeholder="URL base del provider (opzionale)"
                        defaultValue={model?.baseUrl}
                    />
                    <div className='flex items-center gap-3'>
                        <input
                            type="checkbox"
                            name="enabled"
                            id="enabled-edit"
                            defaultChecked={model?.enabled}
                            className='w-4 h-4 rounded border-light focus:ring-0'
                        />
                        <label htmlFor="enabled-edit" className='text-sm'>Attivo</label>
                    </div>

                    <div className='flex justify-between gap-4 mt-6'>
                        <div onClick={() => setVisible(false)} className='text-muted hover:bg-verylight cursor-pointer px-4 py-3 text-sm rounded-sm'>
                            Annulla
                        </div>
                        <Button submit>Conferma</Button>
                    </div>
                </form>
            </div>
        </div>
    )
}
